import { KnowledgeBaseWithDocuments } from '../../types/navigationTypes';
import { getKnowledgeBaseDocumentsForExecution } from '../../features/knowledgebase/services/knowledgeBaseService';
import { supabase } from '../../core/lib/supabase';

/**
 * Información de documento para caché
 */
export interface DocumentInfo {
  id: string;
  name: string;
  pdf_url: string | null;
  source_path?: string | null;
  document_type?: 'assignation' | 'knowledge_base';
}

/**
 * Entrada de caché con timestamp y tiempo de expiración para Knowledge Bases
 */
interface KBCacheEntry {
  data: KnowledgeBaseWithDocuments[];
  timestamp: number;
  expiresIn: number; // milisegundos
}

/**
 * Entrada de caché para documentos individuales de assignation
 */
interface AssignationDocCacheEntry {
  document: DocumentInfo;
  timestamp: number;
  expiresIn: number;
}

/**
 * Servicio centralizado unificado para cachear documentos
 * 
 * Este servicio maneja:
 * - Knowledge Bases con documentos (tabla 'documents')
 * - Documentos de asignación (tabla 'workpill_execution_documents')
 * 
 * Cuando se buscan documentos por ID, el servicio busca primero en
 * 'workpill_execution_documents' y luego en 'documents' (Knowledge Bases)
 * para encontrar documentos de ambos tipos.
 * 
 * Evita cargas duplicadas y mejora la performance compartiendo datos
 * entre componentes (ChatPanel, KnowledgeBasePanel, ListDocumentsResult, etc.)
 */
class UnifiedDocumentCacheService {
  // Cache para Knowledge Bases (existente)
  private kbCache: Map<string, KBCacheEntry> = new Map();
  private kbLoadingPromises: Map<string, Promise<KnowledgeBaseWithDocuments[]>> = new Map();
  
  // Cache para documentos individuales de assignation (nuevo)
  private assignationDocsCache: Map<string, AssignationDocCacheEntry> = new Map();
  private assignationDocsLoadingPromises: Map<string, Promise<DocumentInfo[]>> = new Map();
  
  // Mapa de executionId → Set de document IDs para invalidación rápida
  private executionDocsMap: Map<string, Set<string>> = new Map();
  
  // Tiempo de expiración por defecto: 5 minutos
  private readonly DEFAULT_EXPIRATION = 5 * 60 * 1000;
  
  // ============================================================================
  // KNOWLEDGE BASES CACHE (API existente - compatibilidad hacia atrás)
  // ============================================================================
  
  /**
   * Obtiene documentos de Knowledge Bases con caché automático
   * 
   * @param executionId - ID de la ejecución
   * @param workpillId - ID del workpill
   * @param forceRefresh - Si es true, ignora el caché y recarga
   * @returns Promise con array de Knowledge Bases con documentos
   */
  async getDocuments(
    executionId: string,
    workpillId?: string | null,
    forceRefresh = false
  ): Promise<KnowledgeBaseWithDocuments[]> {
    const wp = workpillId ?? '';
    const cacheKey = this.getKBCacheKey(executionId, wp);
    
    console.log('🗄️ UnifiedDocumentCacheService.getDocuments (KB):', {
      executionId,
      workpillId: wp,
      forceRefresh,
      hasCached: this.kbCache.has(cacheKey)
    });
    
    // Si existe en caché y no está expirado, devolverlo
    if (!forceRefresh) {
      const cached = this.kbCache.get(cacheKey);
      if (cached && this.isKBCacheValid(cached)) {
        console.log('✅ Returning KBs from cache');
        return cached.data;
      } else if (cached) {
        console.log('⏰ KB cache expired, cleaning...');
        this.kbCache.delete(cacheKey);
      }
    }
    
    // Si ya se está cargando, devolver la promesa existente (evitar requests duplicadas)
    if (this.kbLoadingPromises.has(cacheKey)) {
      console.log('⏳ KB load in progress, waiting for existing promise...');
      return this.kbLoadingPromises.get(cacheKey)!;
    }
    
    // Iniciar nueva carga
    console.log('📥 Starting KB load from Supabase...');
    const loadPromise = this.fetchKnowledgeBases(executionId, wp);
    this.kbLoadingPromises.set(cacheKey, loadPromise);
    
    try {
      const data = await loadPromise;
      
      // Guardar en caché
      this.kbCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
        expiresIn: this.DEFAULT_EXPIRATION
      });
      
      console.log(`✅ KBs loaded and cached (${data.length} KBs)`);
      return data;
    } finally {
      // Limpiar la promesa de carga
      this.kbLoadingPromises.delete(cacheKey);
    }
  }
  
  /**
   * Realiza la carga real de Knowledge Bases desde Supabase
   */
  private async fetchKnowledgeBases(
    executionId: string,
    workpillId: string
  ): Promise<KnowledgeBaseWithDocuments[]> {
    return getKnowledgeBaseDocumentsForExecution(executionId, workpillId || undefined);
  }
  
  // ============================================================================
  // ASSIGNATION DOCUMENTS CACHE (NUEVO)
  // ============================================================================
  
  /**
   * Cachea documentos de assignation para una ejecución
   * Se llama automáticamente cuando se cargan documentos desde documentService
   * 
   * @param executionId - ID de la ejecución
   * @param documents - Array de documentos a cachear
   */
  cacheAssignationDocuments(executionId: string, documents: Array<{ id: string; document_name: string; pdf_url?: string | null }>): void {
    console.log(`📦 Caching ${documents.length} assignation documents for execution ${executionId}`);
    
    // Inicializar Set de IDs para esta ejecución si no existe
    if (!this.executionDocsMap.has(executionId)) {
      this.executionDocsMap.set(executionId, new Set());
    }
    const docIdsSet = this.executionDocsMap.get(executionId)!;
    
    // Cachear cada documento
    documents.forEach(doc => {
      const docInfo: DocumentInfo = {
        id: doc.id,
        name: doc.document_name || `Document ${doc.id.substring(0, 8)}...`,
        pdf_url: doc.pdf_url || null,
        source_path: undefined, // Se obtendrá cuando se busque individualmente
        document_type: 'assignation'
      };
      
      this.assignationDocsCache.set(doc.id, {
        document: docInfo,
        timestamp: Date.now(),
        expiresIn: this.DEFAULT_EXPIRATION
      });
      
      docIdsSet.add(doc.id);
    });
    
    console.log(`✅ ${documents.length} documents cached for execution ${executionId}`);
  }
  
  /**
   * Obtiene información de un documento individual desde el caché
   * Si no está en caché, intenta cargarlo desde Supabase
   * Busca primero en workpill_execution_documents, luego en documents (Knowledge Bases)
   * 
   * @param documentId - ID del documento
   * @returns DocumentInfo o null si no se encuentra
   */
  async getAssignationDocument(documentId: string): Promise<DocumentInfo | null> {
    // Intentar desde caché primero
    const cached = this.assignationDocsCache.get(documentId);
    if (cached && this.isAssignationDocCacheValid(cached)) {
      console.log(`✅ Document ${documentId} found in cache`);
      return cached.document;
    } else if (cached) {
      console.log(`⏰ Cache expired for document ${documentId}`);
      this.assignationDocsCache.delete(documentId);
    }
    
    // Si no está en caché, cargar desde Supabase
    console.log(`📥 Loading document ${documentId} from Supabase...`);
    try {
      // Paso 1: Buscar en workpill_execution_documents
      const { data: assignationDoc, error: assignationError } = await supabase
        .from('workpill_execution_documents')
        .select('id, document_name, pdf_url, source_path')
        .eq('id', documentId)
        .maybeSingle();
      
      if (assignationError) {
        console.error(`❌ Error searching for document ${documentId} in workpill_execution_documents:`, assignationError);
      } else if (assignationDoc) {
        // Priorizar source_path sobre pdf_url para generar la URL
        let pdfUrl: string | null = assignationDoc.pdf_url || null;
        
        if (assignationDoc.source_path) {
          // Generar signed URL desde source_path
          pdfUrl = await this.generateAssignationDocumentUrl(assignationDoc.source_path);
        } else if (!pdfUrl) {
          // Si no hay source_path ni pdf_url, intentar construir el path por defecto
          const defaultStoragePath = `${assignationDoc.id}/document.pdf`;
          pdfUrl = await this.generateAssignationDocumentUrl(defaultStoragePath);
        }
        
        const docInfo: DocumentInfo = {
          id: assignationDoc.id,
          name: assignationDoc.document_name || `Document ${assignationDoc.id.substring(0, 8)}...`,
          pdf_url: pdfUrl,
          source_path: assignationDoc.source_path || null,
          document_type: 'assignation'
        };
        
        // Cachear para futuras consultas
        this.assignationDocsCache.set(documentId, {
          document: docInfo,
          timestamp: Date.now(),
          expiresIn: this.DEFAULT_EXPIRATION
        });
        
        return docInfo;
      }
      
      // Paso 2: Si no se encontró en assignation, buscar en nodes (Knowledge Bases)
      console.log(`📥 Document ${documentId} not found in assignation, searching in nodes (KB)...`);
      const { data: kbDoc, error: kbError } = await supabase
        .from('nodes')
        .select('id, name, mime_type')
        .eq('id', documentId)
        .eq('type', 'file')
        .maybeSingle();
      
      if (kbError) {
        console.error(`❌ Error searching for document ${documentId} in nodes:`, kbError);
        return null;
      }
      
      if (!kbDoc) {
        console.warn(`⚠️ Document ${documentId} not found in any table`);
        return null;
      }
      
      // Generar storage_path y signed URL para documento de KB
      const storagePath = `${kbDoc.id}/document`;
      let pdfUrl: string | null = null;
      pdfUrl = await this.generateKBDocumentUrl(storagePath);
      
      const docInfo: DocumentInfo = {
        id: kbDoc.id,
        name: kbDoc.name || `Document ${kbDoc.id.substring(0, 8)}...`,
        pdf_url: pdfUrl,
        source_path: storagePath,
        document_type: 'knowledge_base'
      };
      
      // Cachear para futuras consultas
      this.assignationDocsCache.set(documentId, {
        document: docInfo,
        timestamp: Date.now(),
        expiresIn: this.DEFAULT_EXPIRATION
      });
      
      return docInfo;
    } catch (error) {
      console.error(`❌ Error loading document ${documentId}:`, error);
      return null;
    }
  }
  
  /**
   * Obtiene información de múltiples documentos desde el caché
   * Los documentos que no estén en caché se cargan desde Supabase en batch
   * 
   * @param documentIds - Array de IDs de documentos
   * @returns Array de DocumentInfo (puede incluir documentos con nombres fallback si no se encuentran)
   */
  async getAssignationDocumentsInfo(documentIds: string[]): Promise<DocumentInfo[]> {
    if (!documentIds || documentIds.length === 0) {
      return [];
    }
    
    console.log(`🔍 Fetching info for ${documentIds.length} documents...`);
    
    const results: DocumentInfo[] = [];
    const idsToFetch: string[] = [];
    
    // Separar documentos en caché y documentos a cargar
    documentIds.forEach(id => {
      const cached = this.assignationDocsCache.get(id);
      if (cached && this.isAssignationDocCacheValid(cached)) {
        results.push(cached.document);
      } else {
        if (cached) {
          this.assignationDocsCache.delete(id); // Limpiar expirado
        }
        idsToFetch.push(id);
      }
    });
    
    console.log(`✅ ${results.length} documents from cache, ${idsToFetch.length} to load`);
    
    // Si hay documentos a cargar, hacer batch query
    if (idsToFetch.length > 0) {
      const cacheKey = idsToFetch.sort().join(',');
      
      // Evitar requests duplicados simultáneos
      if (this.assignationDocsLoadingPromises.has(cacheKey)) {
        console.log('⏳ Document load in progress, waiting...');
        const fetchedDocs = await this.assignationDocsLoadingPromises.get(cacheKey)!;
        results.push(...fetchedDocs);
        return results;
      }
      
      const loadPromise = this.fetchAssignationDocuments(idsToFetch);
      this.assignationDocsLoadingPromises.set(cacheKey, loadPromise);
      
      try {
        const fetchedDocs = await loadPromise;
        results.push(...fetchedDocs);
      } finally {
        this.assignationDocsLoadingPromises.delete(cacheKey);
      }
    }
    
    // Ordenar resultados según el orden de los IDs solicitados
    const resultsMap = new Map(results.map(doc => [doc.id, doc]));
    return documentIds.map(id => {
      const doc = resultsMap.get(id);
      if (doc) return doc;
      // Fallback si no se encontró
      return {
        id,
        name: `Document ${id.substring(0, 8)}...`,
        pdf_url: null,
        source_path: null,
        document_type: undefined
      };
    });
  }
  
  /**
   * Genera una signed URL para un documento de Knowledge Base desde su source_path
   */
  private async generateKBDocumentUrl(storagePath: string): Promise<string | null> {
    try {
      const { data: urlData, error: urlError } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 3600); // 1 hora de expiración

      if (urlError) {
        console.warn(`⚠️ No se pudo generar URL para source_path ${storagePath}:`, urlError);
        return null;
      }

      return urlData?.signedUrl || null;
    } catch (error) {
      console.warn(`⚠️ Error generando URL para source_path ${storagePath}:`, error);
      return null;
    }
  }

  /**
   * Genera una signed URL para un documento de assignation desde su source_path
   * El bucket es 'workpill-assignation-documents'
   */
  private async generateAssignationDocumentUrl(storagePath: string): Promise<string | null> {
    try {
      const { data: urlData, error: urlError } = await supabase.storage
        .from('workpill-assignation-documents')
        .createSignedUrl(storagePath, 3600); // 1 hora de expiración

      if (urlError) {
        console.warn(`⚠️ No se pudo generar URL para source_path ${storagePath}:`, urlError);
        return null;
      }

      return urlData?.signedUrl || null;
    } catch (error) {
      console.warn(`⚠️ Error generando URL para source_path ${storagePath}:`, error);
      return null;
    }
  }

  /**
   * Carga documentos desde Supabase en batch
   * Busca primero en workpill_execution_documents, luego en documents (Knowledge Bases)
   */
  private async fetchAssignationDocuments(documentIds: string[]): Promise<DocumentInfo[]> {
    const allDocInfos: DocumentInfo[] = [];
    const foundIds = new Set<string>();
    let assignationDocsCount = 0;
    let kbDocsCount = 0;
    
    try {
      // Paso 1: Buscar en workpill_execution_documents
      console.log(`📥 Searching for ${documentIds.length} documents in workpill_execution_documents...`);
      const { data: assignationDocs, error: assignationError } = await supabase
        .from('workpill_execution_documents')
        .select('id, document_name, pdf_url, source_path')
        .in('id', documentIds);
      
      if (assignationError) {
        console.error('❌ Error fetching assignation documents:', assignationError);
      } else if (assignationDocs && assignationDocs.length > 0) {
        assignationDocsCount = assignationDocs.length;
        console.log(`✅ Found ${assignationDocsCount} documents in workpill_execution_documents`);
        
        // Generar URLs para documentos de assignation en paralelo
        const assignationDocInfosPromises = assignationDocs.map(async (doc) => {
          // Priorizar source_path sobre pdf_url para generar la URL
          let pdfUrl: string | null = doc.pdf_url || null;
          
          if (doc.source_path) {
            // Generar signed URL desde source_path
            pdfUrl = await this.generateAssignationDocumentUrl(doc.source_path);
          } else if (!pdfUrl) {
            // Si no hay source_path ni pdf_url, intentar construir el path por defecto
            const defaultStoragePath = `${doc.id}/document.pdf`;
            pdfUrl = await this.generateAssignationDocumentUrl(defaultStoragePath);
          }
          
          const docInfo: DocumentInfo = {
            id: doc.id,
            name: doc.document_name || `Document ${doc.id.substring(0, 8)}...`,
            pdf_url: pdfUrl,
            source_path: doc.source_path || null,
            document_type: 'assignation'
          };
          
          // Cachear individualmente
          this.assignationDocsCache.set(doc.id, {
            document: docInfo,
            timestamp: Date.now(),
            expiresIn: this.DEFAULT_EXPIRATION
          });
          
          foundIds.add(doc.id);
          return docInfo;
        });
        
        const assignationDocInfos = await Promise.all(assignationDocInfosPromises);
        allDocInfos.push(...assignationDocInfos);
      }
      
      // Paso 2: Identificar IDs no encontrados y buscar en nodes (Knowledge Bases)
      const missingIds = documentIds.filter(id => !foundIds.has(id));
      
      if (missingIds.length > 0) {
        console.log(`📥 Searching for ${missingIds.length} missing documents in nodes (Knowledge Bases)...`);
        
        const { data: kbDocs, error: kbError } = await supabase
          .from('nodes')
          .select('id, name, mime_type')
          .eq('type', 'file')
          .in('id', missingIds);
        
        if (kbError) {
          console.error('❌ Error fetching KB documents from nodes:', kbError);
        } else if (kbDocs && kbDocs.length > 0) {
          kbDocsCount = kbDocs.length;
          console.log(`✅ Found ${kbDocsCount} documents in nodes (Knowledge Bases)`);
          
          // Generar URLs para documentos de KB en paralelo
          const kbDocInfosPromises = kbDocs.map(async (doc) => {
            const storagePath = `${doc.id}/document`;
            let pdfUrl: string | null = null;
            
            pdfUrl = await this.generateKBDocumentUrl(storagePath);
            
            const docInfo: DocumentInfo = {
              id: doc.id,
              name: doc.name || `Document ${doc.id.substring(0, 8)}...`,
              pdf_url: pdfUrl,
              source_path: storagePath,
              document_type: 'knowledge_base'
            };
            
            // Cachear individualmente
            this.assignationDocsCache.set(doc.id, {
              document: docInfo,
              timestamp: Date.now(),
              expiresIn: this.DEFAULT_EXPIRATION
            });
            
            return docInfo;
          });
          
          const kbDocInfos = await Promise.all(kbDocInfosPromises);
          allDocInfos.push(...kbDocInfos);
          
          kbDocs.forEach(doc => foundIds.add(doc.id));
        }
      }
      
      // Paso 3: Crear fallbacks para IDs que no se encontraron en ninguna tabla
      const stillMissingIds = documentIds.filter(id => !foundIds.has(id));
      if (stillMissingIds.length > 0) {
        console.warn(`⚠️ ${stillMissingIds.length} documents not found in any table`);
        const fallbackDocs: DocumentInfo[] = stillMissingIds.map(id => ({
          id,
          name: `Document ${id.substring(0, 8)}...`,
          pdf_url: null,
          source_path: null,
          document_type: undefined
        }));
        allDocInfos.push(...fallbackDocs);
      }
      
      console.log(`✅ Total: ${allDocInfos.length} documents processed (${assignationDocsCount} assignation + ${kbDocsCount} KB)`);
      return allDocInfos;
      
    } catch (error) {
      console.error('❌ Error in fetchAssignationDocuments:', error);
      // En caso de error, devolver fallbacks para todos los IDs
      return documentIds.map(id => ({
        id,
        name: `Document ${id.substring(0, 8)}...`,
        pdf_url: null,
        source_path: null,
        document_type: undefined
      }));
    }
  }
  
  // ============================================================================
  // UTILIDADES Y VALIDACIÓN
  // ============================================================================
  
  /**
   * Verifica si una entrada de caché KB es válida (no expirada)
   */
  private isKBCacheValid(entry: KBCacheEntry): boolean {
    const age = Date.now() - entry.timestamp;
    return age < entry.expiresIn;
  }
  
  /**
   * Verifica si una entrada de caché de documento assignation es válida
   */
  private isAssignationDocCacheValid(entry: AssignationDocCacheEntry): boolean {
    const age = Date.now() - entry.timestamp;
    return age < entry.expiresIn;
  }
  
  /**
   * Genera la clave de caché para Knowledge Bases
   */
  private getKBCacheKey(executionId: string, workpillId: string): string {
    return `kb:${executionId}-${workpillId}`;
  }
  
  // ============================================================================
  // INVALIDACIÓN Y LIMPIEZA
  // ============================================================================
  
  /**
   * Invalida el caché para una ejecución específica (KB y assignation docs)
   * Útil cuando se sabe que los documentos han cambiado
   */
  invalidateCache(executionId: string, workpillVersion?: string | null): void {
    const wp = workpillVersion ?? '';
    const kbCacheKey = this.getKBCacheKey(executionId, wp);
    console.log('🗑️ Invalidando caché para:', { executionId, workpillId: wp });
    
    // Invalidar KB cache
    this.kbCache.delete(kbCacheKey);
    
    // Invalidar assignation documents de esta ejecución
    const docIds = this.executionDocsMap.get(executionId);
    if (docIds) {
      docIds.forEach(docId => {
        this.assignationDocsCache.delete(docId);
      });
      this.executionDocsMap.delete(executionId);
      console.log(`🗑️ Invalidados ${docIds.size} documentos de assignation`);
    }
  }
  
  /**
   * Invalida solo los documentos de assignation de una ejecución
   */
  invalidateAssignationDocuments(executionId: string): void {
    const docIds = this.executionDocsMap.get(executionId);
    if (docIds) {
      docIds.forEach(docId => {
        this.assignationDocsCache.delete(docId);
      });
      this.executionDocsMap.delete(executionId);
      console.log(`🗑️ Invalidados ${docIds.size} documentos de assignation para ejecución ${executionId}`);
    }
  }
  
  /**
   * Limpia todo el caché (KB y assignation docs)
   * Útil al cerrar sesión o cambiar de usuario
   */
  clearAllCache(): void {
    console.log('🗑️ Limpiando todo el caché de documentos');
    this.kbCache.clear();
    this.kbLoadingPromises.clear();
    this.assignationDocsCache.clear();
    this.assignationDocsLoadingPromises.clear();
    this.executionDocsMap.clear();
  }
  
  /**
   * Obtiene estadísticas del caché (para debugging)
   */
  getCacheStats(): {
    kbEntries: number;
    assignationDocEntries: number;
    executionMappings: number;
    kbLoading: number;
    assignationDocsLoading: number;
    details: {
      kb: Array<{ key: string; age: number; valid: boolean }>;
      assignationDocs: number;
    };
  } {
    const kbDetails = Array.from(this.kbCache.entries()).map(([key, entry]) => ({
      key,
      age: Date.now() - entry.timestamp,
      valid: this.isKBCacheValid(entry)
    }));
    
    return {
      kbEntries: this.kbCache.size,
      assignationDocEntries: this.assignationDocsCache.size,
      executionMappings: this.executionDocsMap.size,
      kbLoading: this.kbLoadingPromises.size,
      assignationDocsLoading: this.assignationDocsLoadingPromises.size,
      details: {
        kb: kbDetails,
        assignationDocs: this.assignationDocsCache.size
      }
    };
  }
  
  /**
   * Pre-carga documentos en segundo plano
   * Útil para pre-cargar mientras el usuario está haciendo otra cosa
   */
  async preloadDocuments(executionId: string, workpillId?: string | null): Promise<void> {
    console.log('🔄 Pre-loading documents in background...');
    try {
      await this.getDocuments(executionId, workpillId ?? '');
    } catch (error) {
      console.warn('⚠️ Error pre-loading documents (non-critical):', error);
    }
  }
}

// Exportar instancia singleton (mantener nombre para compatibilidad)
export const documentCacheService = new UnifiedDocumentCacheService();








